import { useEffect, useState } from "react";
import { Loader2, Save, SlidersHorizontal, ListChecks, Check, Pencil, Ban, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, fmtMoney, formatApiError } from "@/lib/api";

/* ============================ P1 — Pricing config + Service catalog ============================ */

const NUM = (v, d = 0) => (v === "" || v == null || isNaN(Number(v)) ? d : Number(v));

export function PricingCatalogPanel() {
  const [cfg, setCfg] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);
  const [savingCat, setSavingCat] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/admin/pricing-settings"), api.get("/admin/service-catalog")])
      .then(([p, c]) => { setCfg(p.data.config); setCatalog(c.data.catalog); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!cfg || !catalog) return null;

  const setC = (k, v) => setCfg((x) => ({ ...x, [k]: v }));
  const marginPct = Math.round((cfg.target_gross_margin ?? 0.6) * 100);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const payload = {
        currency: cfg.currency, labor_rate: NUM(cfg.labor_rate, 75),
        specialist_rate: NUM(cfg.specialist_rate, 125),
        target_gross_margin: Math.min(0.95, Math.max(0, NUM(marginPct, 60) / 100)),
        min_price: NUM(cfg.min_price, 500), recurring_min: NUM(cfg.recurring_min, 250),
        rounding: Math.max(1, NUM(cfg.rounding, 50)),
      };
      const { data } = await api.put("/admin/pricing-settings", { config: payload });
      setCfg(data.config);
      toast.success("Pricing rules saved — new proposals use them; existing proposals keep their snapshot.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save."); }
    finally { setSavingCfg(false); }
  };

  const setSvc = (i, patch) => setCatalog((list) => list.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const saveCatalog = async () => {
    // client-side sanity: max >= min, base within bounds
    for (const s of catalog) {
      const mn = NUM(s.min_hours), mx = NUM(s.max_hours);
      if (mx < mn) { toast.error(`${s.name}: max hours must be ≥ min hours.`); return; }
    }
    setSavingCat(true);
    try {
      const { data } = await api.put("/admin/service-catalog", { catalog });
      setCatalog(data.catalog);
      toast.success("Service catalog saved — this is the authoritative source for proposal pricing.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save catalog."); }
    finally { setSavingCat(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8 mb-8 shadow-sm" data-testid="pricing-catalog-panel">
      <div className="flex items-center gap-2 mb-1">
        <SlidersHorizontal className="w-5 h-5 text-indigo-600" />
        <h2 className="font-heading text-xl font-semibold text-slate-900">Service Pricing & Catalog</h2>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        The <b>authoritative source</b> for proposal pricing. Rates, margin and hour bounds live here — never
        hard-coded. AI may refine hours within each service's bounds, but the dollar math is always deterministic.
      </p>

      {/* Global pricing rules */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Field label="Currency">
          <Input data-testid="price-currency" value={cfg.currency || "USD"} onChange={(e) => setC("currency", e.target.value.toUpperCase())} />
        </Field>
        <Field label="Internal labor rate ($/hr)">
          <Input data-testid="price-labor-rate" type="number" min="1" value={cfg.labor_rate} onChange={(e) => setC("labor_rate", e.target.value)} />
        </Field>
        <Field label="Specialist rate ($/hr)">
          <Input data-testid="price-specialist-rate" type="number" min="1" value={cfg.specialist_rate} onChange={(e) => setC("specialist_rate", e.target.value)} />
        </Field>
        <Field label="Target gross margin (%)">
          <Input data-testid="price-margin" type="number" min="0" max="95" value={marginPct}
            onChange={(e) => setC("target_gross_margin", Math.min(0.95, Math.max(0, NUM(e.target.value) / 100)))} />
        </Field>
        <Field label="Minimum one-time price ($)">
          <Input data-testid="price-min" type="number" min="0" step="50" value={cfg.min_price} onChange={(e) => setC("min_price", e.target.value)} />
        </Field>
        <Field label="Minimum recurring ($/mo)">
          <Input data-testid="price-recurring-min" type="number" min="0" step="50" value={cfg.recurring_min} onChange={(e) => setC("recurring_min", e.target.value)} />
        </Field>
        <Field label="Round prices to nearest ($)">
          <Input data-testid="price-rounding" type="number" min="1" step="1" value={cfg.rounding} onChange={(e) => setC("rounding", e.target.value)} />
        </Field>
      </div>
      <Button onClick={saveConfig} disabled={savingCfg} data-testid="price-save"
        className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 mb-8">
        {savingCfg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save pricing rules
      </Button>

      {/* Service catalog */}
      <div className="border-t border-slate-100 pt-6">
        <h3 className="font-heading font-semibold text-slate-900 mb-1">Service definitions & hour bounds</h3>
        <p className="text-xs text-slate-500 mb-4">Hours scale with the number & severity of findings, always clamped to each service's min/max.</p>
        <div className="space-y-3">
          {catalog.map((s, i) => <ServiceRow key={s.id || i} s={s} onChange={(patch) => setSvc(i, patch)} idx={i} />)}
        </div>
        <Button onClick={saveCatalog} disabled={savingCat} data-testid="catalog-save"
          className="mt-6 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6">
          {savingCat ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save service catalog
        </Button>
      </div>
    </div>
  );
}

function ServiceRow({ s, onChange, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200" data-testid={`catalog-service-${s.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
        <div className="min-w-0">
          <p className="font-medium text-slate-900 truncate">{s.name}</p>
          <p className="text-xs text-slate-500">{s.base_hours ?? s.hours}h base · {s.min_hours ?? "–"}–{s.max_hours ?? "–"}h · {s.pricing_type || "hourly"}{s.recurring ? " · recurring" : ""}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 grid sm:grid-cols-3 gap-3 border-t border-slate-100 pt-3">
          <Field label="Base hours"><Input data-testid={`svc-${s.id}-base`} type="number" min="0" value={s.base_hours ?? s.hours ?? 0} onChange={(e) => onChange({ base_hours: NUM(e.target.value), hours: NUM(e.target.value) })} /></Field>
          <Field label="Min hours"><Input data-testid={`svc-${s.id}-min`} type="number" min="0" value={s.min_hours ?? 0} onChange={(e) => onChange({ min_hours: NUM(e.target.value) })} /></Field>
          <Field label="Max hours"><Input data-testid={`svc-${s.id}-max`} type="number" min="0" value={s.max_hours ?? 0} onChange={(e) => onChange({ max_hours: NUM(e.target.value) })} /></Field>
          <Field label="Hours / extra finding"><Input data-testid={`svc-${s.id}-perfinding`} type="number" min="0" step="0.5" value={s.hours_per_finding ?? 0} onChange={(e) => onChange({ hours_per_finding: NUM(e.target.value) })} /></Field>
          <Field label="High-severity uplift (h)"><Input data-testid={`svc-${s.id}-severity`} type="number" min="0" step="0.5" value={s.severity_hours ?? 0} onChange={(e) => onChange({ severity_hours: NUM(e.target.value) })} /></Field>
          <Field label="Specialist hours"><Input type="number" min="0" value={s.specialist_hours ?? 0} onChange={(e) => onChange({ specialist_hours: NUM(e.target.value) })} /></Field>
          <Field label="Hard costs ($)"><Input type="number" min="0" value={s.hard_costs ?? 0} onChange={(e) => onChange({ hard_costs: NUM(e.target.value) })} /></Field>
          <Field label="Per-service min price ($)"><Input type="number" min="0" value={s.min_service_price ?? ""} placeholder="(use global)" onChange={(e) => onChange({ min_service_price: e.target.value === "" ? null : NUM(e.target.value) })} /></Field>
          <Field label="Timeframe"><Input value={s.timeframe || ""} onChange={(e) => onChange({ timeframe: e.target.value })} /></Field>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 sm:col-span-1">
            <span className="text-xs font-medium text-slate-600">Recurring service</span>
            <Switch data-testid={`svc-${s.id}-recurring`} checked={!!s.recurring} onCheckedChange={(v) => onChange({ recurring: v })} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================ P2 — Provisional review queue ============================ */

export function ReviewQueuePanel() {
  const [queue, setQueue] = useState(null);
  const load = () => api.get("/admin/review-queue").then(({ data }) => setQueue(data.queue || [])).catch(() => setQueue([]));
  useEffect(() => { load(); }, []);

  const total = (queue || []).reduce((n, p) => n + (p.provisional_items?.length || 0), 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8 mb-8 shadow-sm" data-testid="review-queue-panel">
      <div className="flex items-center gap-2 mb-1">
        <ListChecks className="w-5 h-5 text-amber-600" />
        <h2 className="font-heading text-xl font-semibold text-slate-900">Provisional Pricing Review</h2>
        {total > 0 && <span className="rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5">{total} pending</span>}
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Items the engine couldn't confidently scope. A customer <b>cannot pay</b> a proposal until every
        provisional item here is approved, edited, or rejected — AI estimates never auto-finalize.
      </p>
      {!queue ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> :
        queue.length === 0 ? <p className="text-sm text-slate-400" data-testid="review-queue-empty">Nothing pending review. All proposals are clear.</p> : (
          <div className="space-y-5">
            {queue.map((p) => (
              <div key={p.public_id} className="rounded-xl border border-slate-200 p-4" data-testid={`review-proposal-${p.public_id}`}>
                <p className="font-medium text-slate-900">{p.business_name || p.title || p.public_id}</p>
                <p className="text-xs text-slate-500 mb-3">{p.public_id} · {p.provisional_items.length} item(s) need review</p>
                <div className="space-y-3">
                  {p.provisional_items.map((it) => (
                    <ReviewItem key={it.line_id} pid={p.public_id} it={it} onDone={load} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function ReviewItem({ pid, it, onDone }) {
  const sa = it.scope_audit || {};
  const hrs = sa.hours || {};
  const baseline = hrs.baseline || {};
  const ai = hrs.ai_refinement || {};
  const bounds = ai.bounds || { min: baseline.min_hours, max: baseline.max_hours };
  const [editing, setEditing] = useState(false);
  const [hours, setHours] = useState(hrs.applied_hours ?? baseline.baseline_hours ?? bounds.min ?? 0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    if (action === "edit") {
      const h = Number(hours);
      if (isNaN(h) || h < bounds.min || h > bounds.max) {
        toast.error(`Hours must be within ${bounds.min}–${bounds.max} for this service.`); return;
      }
    }
    setBusy(true);
    try {
      const body = { action, note };
      if (action === "edit") body.hours = Number(hours);
      await api.post(`/admin/proposals/${pid}/review-line/${it.line_id}`, body);
      toast.success(action === "reject" ? "Item rejected & removed from totals" : action === "edit" ? "Hours updated & re-priced" : "Item approved");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid={`review-item-${pid}-${it.line_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-slate-900">{it.name} <span className="text-xs text-amber-700">({it.priority})</span></p>
          {(it.review_reasons || []).map((r, i) => <p key={i} className="text-xs text-amber-800 mt-0.5">⚠ {r}</p>)}
        </div>
        <span className="shrink-0 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase px-2 py-0.5">Provisional</span>
      </div>

      <div className="mt-2 text-xs text-slate-600 space-y-1 rounded-md bg-white border border-slate-200 p-2.5">
        <div><b>Baseline:</b> {baseline.breakdown || `${baseline.baseline_hours ?? "?"}h`}</div>
        {ai.available ? (
          <div><b>AI estimate:</b> {ai.ai_hours_raw}h → applied {ai.applied_hours}h (conf {ai.confidence}{ai.clamped_to_bounds ? ", clamped" : ""}) · <span className="italic">{ai.reasoning}</span></div>
        ) : <div><b>AI:</b> {ai.note || "not applied"}</div>}
        <div><b>Bounds:</b> {bounds.min}–{bounds.max}h · <b>Current price:</b> {fmtMoney(it.pricing?.client_price, (it.pricing?.currency || "USD").toUpperCase())} · <b>Delivery cost:</b> {fmtMoney(it.pricing?.delivery_cost, (it.pricing?.currency || "USD").toUpperCase())} · <b>Margin:</b> {Math.round((it.pricing?.gross_margin || 0) * 100)}%</div>
      </div>

      {editing && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[11px] text-slate-500">Set hours ({bounds.min}–{bounds.max})</Label>
            <Input data-testid={`review-hours-${pid}-${it.line_id}`} type="number" min={bounds.min} max={bounds.max} value={hours} onChange={(e) => setHours(e.target.value)} className="h-9 w-28" />
          </div>
          <Input data-testid={`review-note-${pid}-${it.line_id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="h-9 flex-1 min-w-[160px]" />
          <Button size="sm" disabled={busy} onClick={() => act("edit")} data-testid={`review-save-edit-${pid}-${it.line_id}`} className="bg-indigo-600 hover:bg-indigo-700 h-9">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save & re-price"}
          </Button>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={() => act("approve")} data-testid={`review-approve-${pid}-${it.line_id}`} className="bg-emerald-600 hover:bg-emerald-700 h-8">
          <Check className="w-3.5 h-3.5 mr-1" /> Approve as-is
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setEditing((v) => !v)} data-testid={`review-edit-${pid}-${it.line_id}`} className="h-8">
          <Pencil className="w-3.5 h-3.5 mr-1" /> {editing ? "Cancel edit" : "Edit hours"}
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => act("reject")} data-testid={`review-reject-${pid}-${it.line_id}`} className="h-8 text-red-600 border-red-200">
          <Ban className="w-3.5 h-3.5 mr-1" /> Reject
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
